import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { fetchUserAgencies, acceptAgencyInvite, fetchNotificationPreferences, updateNotificationPreferences } from "../api/backend";
import {
  fetchUserProfile,
  updateUserProfile,
  fetchMyTenantRoles,
  acceptRoleInvitation,
  declineRoleInvitation,
} from "../api/apiClient";
import { useTenant } from "../contexts/TenantContext";
import { getCurrentUser, setCurrentUser } from "../utils/auth";
import ProfileHeader from "./profile/ProfileHeader";
import ProfileSideNav from "./profile/ProfileSideNav";
import { PersonalCard, ContactCard } from "./profile/ProfileDetailsCards";
import SecurityCards from "./profile/SecurityCards";
import NotificationsCard from "./profile/NotificationsCard";
import CentersCard from "./profile/CentersCard";
import AgenciesCard from "./profile/AgenciesCard";
import { PasswordDialog, EmailDialog, TwoFactorDialog, SessionsDialog } from "./profile/ProfileDialogs";
import { editablePayload, missingContactFields, roleLabels, withNotificationToggle } from "./profile/profileModel";
import "./profile/profile.css";

const noop = () => {};
const failureMessage = (error, fallback) => error?.response?.data?.message || error?.response?.data?.error || fallback;

function downloadProfileJson(details) {
  const blob = new Blob([JSON.stringify(details, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `profile-${details.username || "me"}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function ProfilePage() {
  const { isFeatureEnabled } = useTenant();
  const agencyManagementEnabled = isFeatureEnabled("agencyManagement");
  const [user] = useState(() => getCurrentUser());

  const [section, setSection] = useState("profile");
  const [editing, setEditing] = useState(null); // "personal" | "contact" | null
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null); // "password" | "email" | "mfa" | "sessions" | null
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [notificationCategories, setNotificationCategories] = useState([]);
  const [prefs, setPrefs] = useState({});

  const [details, setDetails] = useState({});
  const [savedDetails, setSavedDetails] = useState({});
  const [agencyRows, setAgencyRows] = useState([]);
  const [tenantRoles, setTenantRoles] = useState({});
  const [tenantNames, setTenantNames] = useState({});
  const [invitations, setInvitations] = useState([]);
  // Bumps on each load or save so a profile GET that returns after a save cannot put the old phone back.
  const profileRequest = useRef(0);

  const notify = useCallback((message, severity = "success") => setSnackbar({ open: true, message, severity }), []);

  // Roles decide which notification categories exist, so a role change reloads both.
  const reloadTenantRoles = useCallback(() => {
    fetchMyTenantRoles()
      .then((d) => {
        setTenantRoles(d.tenantRoles || {});
        setTenantNames(d.tenantNames || {});
        setInvitations(d.pendingInvitations || []);
      })
      .catch(noop);
    fetchNotificationPreferences(user.id)
      .then((d) => {
        setNotificationCategories(d.categories || []);
        setPrefs(d.preferences || {});
      })
      .catch(noop);
  }, [user]);

  useEffect(() => {
    if (user) reloadTenantRoles();
  }, [user, reloadTenantRoles]);

  useEffect(() => {
    if (!user) return;
    const request = ++profileRequest.current;
    fetchUserProfile(user.id)
      .then((p) => {
        if (request !== profileRequest.current) return;
        setDetails(p || {});
        setSavedDetails(p || {});
      })
      .catch((error) => {
        if (request !== profileRequest.current) return;
        console.error(error);
        setDetails({});
        setSavedDetails({});
        notify("We couldn't load your profile right now. Please try again.", "warning");
      });
  }, [user, notify]);

  useEffect(() => {
    if (user && agencyManagementEnabled) fetchUserAgencies(user.id).then(setAgencyRows).catch(noop);
  }, [user, agencyManagementEnabled]);

  const missing = useMemo(() => missingContactFields(details), [details]);
  const pendingAgencies = agencyRows.filter((r) => r.status === "PENDING" || r.status === "INVITED").length;

  async function acceptAgency(membershipId) {
    try {
      setAgencyRows(await acceptAgencyInvite(user.id, membershipId));
      notify("You joined the agency.", "success");
    } catch (e) {
      notify(failureMessage(e, "We couldn't accept the invitation. Please try again."), "error");
    }
  }

  // Optimistic: flip the switch now, put it back if the server refuses.
  async function toggleEmail(key, value) {
    const before = prefs;
    setPrefs((p) => withNotificationToggle(p, key, "email", value));
    try {
      const saved = await updateNotificationPreferences(user.id, { [key]: { email: value } });
      setPrefs(saved.preferences || {});
    } catch (e) {
      setPrefs(before);
      notify(failureMessage(e, "Couldn't save that notification setting."), "error");
    }
  }

  const navItems = useMemo(() => {
    const items = [
      { id: "profile", label: "Profile", Icon: PersonOutlineIcon, badge: missing.length },
      { id: "security", label: "Security", Icon: LockOutlinedIcon },
      { id: "notifications", label: "Notifications", Icon: NotificationsNoneIcon },
      { id: "centers", label: "Training centers", Icon: SchoolOutlinedIcon, badge: invitations.length },
    ];
    if (agencyManagementEnabled) items.push({ id: "agencies", label: "Agencies", Icon: BusinessOutlinedIcon, badge: pendingAgencies });
    return items;
  }, [missing.length, invitations.length, pendingAgencies, agencyManagementEnabled]);

  const activeSection = navItems.some((i) => i.id === section) ? section : "profile";

  const startEdit = useCallback((card) => { setSection("profile"); setEditing(card); }, []);
  const cancelEdit = useCallback(() => { setDetails(savedDetails); setEditing(null); }, [savedDetails]);

  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !dialog) cancelEdit(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [editing, dialog, cancelEdit]);

  if (!user) return null;

  const onChange = (field) => (e) => setDetails((d) => ({ ...d, [field]: e.target.value }));

  const save = async () => {
    const request = ++profileRequest.current;
    setSaving(true);
    try {
      const updated = await updateUserProfile(user.id, editablePayload(details));
      if (request !== profileRequest.current) return;
      // Merge: the PUT response only carries editable fields, so fields like mfaEnabled must survive.
      setDetails((d) => ({ ...d, ...updated }));
      setSavedDetails((d) => ({ ...d, ...updated }));
      setCurrentUser({ ...user, email: updated.email || user.email, fullName: updated.fullName || user.fullName, username: updated.username || user.username });
      setEditing(null);
      notify("Profile saved successfully.");
    } catch (error) {
      console.error(error);
      notify(error?.response?.data?.error || error?.message || "Could not save your profile. Please try again later.", "error");
    } finally {
      setSaving(false);
    }
  };

  const closeDialog = () => setDialog(null);
  const displayName = details.fullName || user.name || user.username || "Member";
  const memberId = user.id ? String(user.id).slice(0, 8).toUpperCase() : "N/A";
  const email = details.email || user.email;
  const cardProps = { details, editing: false, saving, onCancel: cancelEdit, onSave: save, onChange };

  return (
    <div className="pf">
      <main className="pf-page" data-testid="profile-page">
        <ProfileHeader name={displayName} memberId={memberId} roles={roleLabels(details.roles)} onDownload={() => downloadProfileJson(details)} />
        <div className="pf-lay">
          <ProfileSideNav items={navItems} active={activeSection} onSelect={(id) => { setEditing(null); setDetails(savedDetails); setSection(id); }} />
          <div>
            {activeSection === "profile" && (
              <>
                {missing.length > 0 && !editing && (
                  <div className="pf-gap" data-testid="profile-missing-banner">
                    <span><b>Your profile is missing a {missing.join(" and ")}.</b> Coordinators use these to reach you about class changes.</span>
                    <button type="button" className="pf-btn pf-btn-o pf-btn-sm" onClick={() => startEdit("contact")}>Add now</button>
                  </div>
                )}
                <PersonalCard {...cardProps} memberId={memberId} editing={editing === "personal"} onEdit={() => startEdit("personal")} />
                <ContactCard {...cardProps} email={email} editing={editing === "contact"} onEdit={() => startEdit("contact")} onChangeEmail={() => setDialog("email")} />
              </>
            )}
            {activeSection === "security" && (
              <SecurityCards
                mfaEnabled={Boolean(details.mfaEnabled)}
                onChangePassword={() => setDialog("password")}
                onToggleMfa={() => setDialog("mfa")}
                onManageSessions={() => setDialog("sessions")}
              />
            )}
            {activeSection === "notifications" && (
              <NotificationsCard categories={notificationCategories} prefs={prefs} onToggle={toggleEmail} />
            )}
            {activeSection === "centers" && (
              <CentersCard
                tenantRoles={tenantRoles}
                tenantNames={tenantNames}
                invitations={invitations}
                onAccept={(id) => acceptRoleInvitation(id)
                  .then(() => { reloadTenantRoles(); notify("Invitation accepted."); })
                  .catch((e) => notify(failureMessage(e, "Couldn't accept that invitation."), "error"))}
                onDecline={(id) => declineRoleInvitation(id)
                  .then(() => { reloadTenantRoles(); notify("Invitation declined."); })
                  .catch((e) => notify(failureMessage(e, "Couldn't decline that invitation."), "error"))}
              />
            )}
            {activeSection === "agencies" && <AgenciesCard rows={agencyRows} onAcceptInvite={acceptAgency} />}
          </div>
        </div>
      </main>

      {dialog === "password" && (
        <PasswordDialog userId={user.id} onClose={closeDialog} onDone={(msg) => { closeDialog(); notify(msg); }} />
      )}
      {dialog === "email" && (
        <EmailDialog
          userId={user.id}
          currentEmail={email}
          onClose={closeDialog}
          onSaved={(newEmail) => {
            setDetails((d) => ({ ...d, email: newEmail }));
            setSavedDetails((d) => ({ ...d, email: newEmail }));
            setCurrentUser({ ...getCurrentUser(), email: newEmail });
            closeDialog();
            notify("Email updated.");
          }}
        />
      )}
      {dialog === "mfa" && (
        <TwoFactorDialog
          mfaEnabled={Boolean(details.mfaEnabled)}
          onClose={closeDialog}
          onChanged={(enabled, msg) => {
            setDetails((d) => ({ ...d, mfaEnabled: enabled }));
            setSavedDetails((d) => ({ ...d, mfaEnabled: enabled }));
            closeDialog();
            notify(msg);
          }}
        />
      )}
      {dialog === "sessions" && <SessionsDialog userId={user.id} onClose={closeDialog} onNotify={notify} />}

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
}
